<template>
    <div class="card" v-show="loaded">

        <div class="card-header border-0">
            <div class="row">
                <div class="col-6">
                    <h3 class="mb-0 text-uppercase">{{ $t(type + '.import') }}</h3>
                </div>
                <div class="col-6 text-right">

                    <base-button type="primary" size="sm" @click="checkFile()">
                        <span class="btn-inner--icon"><i class="fas fa-check"></i></span>
                        <span class="btn-inner--text">{{ $t('import.check-file') }}</span>
                    </base-button>

                    <base-button type="primary" size="sm" @click="importFile()" v-if="type == 'users'">
                        <span class="btn-inner--icon"><i class="fas fa-upload"></i></span>
                        <span class="btn-inner--text">{{ $t('import.import-file') }}</span>
                    </base-button>

                    <base-button type="danger" size="sm" @click="clear()" v-if="canClear">
                        <span class="btn-inner--icon"><i class="tim-icons icon-simple-remove"></i></span>
                        <span class="btn-inner--text">{{ $t('import.clear-history') }}</span>
                    </base-button>
                </div>
            </div>
        </div>

        <p class="card-body pt-0 text-sm" v-if="empty">{{ $t('import.empty') }}</p>

        <div class="pt-2" v-if="!empty">
            
            <table-control :paging="ctrl.paging"></table-control>

            <el-table class="table-responsive table-flush"
                    header-row-class-name="thead-light"
                    :data="ctrl.rows">

                <el-table-column :label="$t('common.date')">
                    <template v-slot="{row}">
                        <span class="font-weight-bold">{{ time(row) }}</span>
                        <br><small>{{ date(row) }}</small>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.source')">
                    <template v-slot="{row}">
                        <div v-html="sourceHtml(row)"></div>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.type')">
                    <template v-slot="{row}">
                        <div v-html="typeHtml(row)"></div>
                    </template>
                </el-table-column>

                <el-table-column :label="$t('common.message')">
                    <template v-slot="{row}">
                        <div v-html="messageHtml(row)"></div>
                    </template>
                </el-table-column>

                <el-table-column align="right" class-name="actions">
                    <template v-slot="{row}">
                        <div class="p-1">
                            <base-button type="primary" size="sm" @click="openDetails(row)" v-if="hasDetails(row)">
                                {{ $t('dashboard.more') }}
                            </base-button>
                        </div>
                    </template>
                </el-table-column>

            </el-table>

            <table-pagination :paging="ctrl.paging"></table-pagination>
        </div>

        <portal to="modals">

            <confirm-modal v-model="ctrl.modals.confirmClear">
                <p>{{ $t('import.clear-history-confirm') }}</p>
            </confirm-modal>

            <check-modal v-model="ctrl.modals.check" :type="type">
            </check-modal>

            <import-modal v-model="ctrl.modals.import" :type="type">
            </import-modal>

            <details-modal v-model="ctrl.modals.details" :log="selectedLog">
            </details-modal>

        </portal>

    </div>
</template>

<script>
    import CrudController from '@trax/classes/CrudController'
    import ImportModal from './ImportModal'
    import CheckModal from './ImportCheckModal'
    import DetailsModal from './ImportDetailsModal'
    import moment from 'moment'

    export default {

        components: {
            ImportModal, CheckModal, DetailsModal
        },

        props: {

            type: {
                type: String,
                required: true
            }
        },

        data() {
            const filters = { 
                type: { $in: ['import-' + this.type, 'check-' + this.type] }
            }

            return {
                moment,
                filters,
                loaded: false,
                ctrl: new CrudController({
                    endpoint: '/astral/api/front/imports',
                    mapData: this.mapData,
                    modals: ['import', 'check', 'details', 'confirmClear'],
                    paging: {
                        perPage: 10,
                        perPageOptions: [5, 10, 25, 50],
                        total: 0
                    },
                    query: {
                        sort: ['-created_at'],
                        relations: ['user', 'owner'],
                        filters
                    },
                }),
                selectedLog: null
            }
        },

        computed: {
            empty() {
                return this.ctrl.rows.length == 0
            },

            canClear() {
                return !this.empty && !this.$auth.user.owner_id
            }
        },

        created() {
            this.ctrl.init(this, 'ctrl').then(resp => {
                this.loaded = true
            })
        },

        methods: {

            date(row) {
                return this.moment(row.created_at).format('YYYY/MM/DD')
            },

            time(row) {
                return this.moment(row.created_at).format('HH:mm')
            },

            sourceHtml(row) {
                if (!row.user_id) {
                    return '<span class="font-weight-bold">Console</span>'
                }
                let html = '<span class="font-weight-bold">' + row.user.name + '</span>'
                if (row.owner_id) {
                    html += '<br><small>' + row.owner.name + '</small>'
                }
                return html
            },

            typeHtml(row) {
                if (row.type == 'check-' + this.type) {
                    return 'Check'
                }
                return '<span class="font-weight-bold">Import</span>'
            },

            messageHtml(row) {
                if (row.status == 'passed') {
                    return '<span class="text-success">OK</span>'
                }
                return '<span class="text-danger">' + row.data.message + '</span>'
            },

            hasDetails(row) {
                return row.data.errors || row.data.feedback
            },

            openDetails(row) {
                this.selectedLog = row
                this.ctrl.modals.open('details')
            },

            checkFile() {
                this.ctrl.modals.open('check').then(resp => {
                    if (resp.changed) {
                        this.ctrl.list()
                    }
                })
            },

            importFile() {
                this.ctrl.modals.open('import').then(resp => {
                    if (resp.changed) {
                        this.ctrl.list()
                    }
                })
            },

            clear() {
                this.ctrl.modals.open('confirmClear').then(resp => {
                    if (resp.confirmed) {
                        this.$loader.show()
                        axios.delete(
                            '/astral/api/front/imports',
                            { params: { 
                                filters: this.filters
                            }}
                        )
                        .then(response => {
                            this.ctrl.list()
                            this.$loader.hide()
                        }).catch(error => {
                            this.ctrl.list()
                            this.$loader.hide()
                            //this.$auth.handleErrorVm(error, this)
                        })
                    }
                })
            },

            mapData(item) {
                return item
            },
        }
    }
</script>

<style scoped>

    .card >>> td {
        padding-right: 0;
    }
    .card >>> td.actions {
        padding-left: 0;
        padding-right: 1.5rem;
    }

</style>
